import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api } from "@/lib/api";
import { PIPELINE, needLabel } from "@/lib/crmConstants";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Columns shown on the board (main commercial flow)
const COLS = ["nouveau_lead", "email_verifie", "a_qualifier", "qualifie", "a_contacter", "contacte", "devis_demande", "devis_transmis", "en_reflexion", "souscription_en_cours", "gagne", "perdu"];

export default function Pipeline() {
  const nav = useNavigate();
  const { user } = useAuth();
  const readOnly = user.role === "read_only";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/crm/prospects").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const onDrop = async (status) => {
    if (!dragId || readOnly) return;
    const prospect = items.find((p) => p.id === dragId);
    if (!prospect || prospect.status === status) { setDragId(null); return; }
    setItems((arr) => arr.map((p) => (p.id === dragId ? { ...p, status } : p)));
    try { await api.patch(`/crm/prospects/${dragId}`, { status }); toast.success("Statut mis à jour."); }
    catch { toast.error("Erreur de mise à jour."); load(); }
    setDragId(null);
  };

  if (loading) return <CrmLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" /></div></CrmLayout>;

  return (
    <CrmLayout>
      <h1 className="font-display text-2xl font-bold text-slate-900">Pipeline commercial</h1>
      <p className="text-slate-500 text-sm mt-1">{readOnly ? "Lecture seule." : "Glissez-déposez les cartes pour changer de statut."}</p>
      <div className="mt-6 overflow-x-auto pb-4" data-testid="crm-kanban-board">
        <div className="flex gap-4 min-w-max">
          {COLS.map((key) => {
            const col = PIPELINE.find((p) => p.key === key);
            const cards = items.filter((p) => p.status === key);
            return (
              <div key={key} className="w-72 shrink-0" onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(key)}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><span className={`w-2.5 h-2.5 rounded-full ${col.color}`} />{col.label}</span>
                  <Badge variant="secondary" className="text-xs">{cards.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[120px] bg-slate-100/60 rounded-xl p-2">
                  {cards.map((p) => (
                    <Card key={p.id} draggable={!readOnly} onDragStart={() => setDragId(p.id)} onClick={() => nav(`/espace-courtier/prospects/${p.id}`)}
                      className="p-3 cursor-pointer hover:shadow-md transition-shadow bg-white" data-testid={`crm-lead-card-${p.id}`}>
                      <p className="text-sm font-medium text-slate-900 truncate">{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}</p>
                      <p className="text-xs text-slate-500 truncate">{p.email}</p>
                      <div className="flex items-center justify-between mt-2">
                        <Badge variant="outline" className="text-[10px]">{needLabel(p.need_type)}</Badge>
                        {p.budget && <span className="text-xs font-mono-crm text-slate-500">{p.budget}€</span>}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CrmLayout>
  );
}
